import { API_TYPES } from "@src/appV2/api";
import { DayOfWeek } from "@src/appV2/InterviewSettings/constants";
import { InterviewType } from "@src/appV2/PlacementApplications/types/fetchInterviews.schema";
import { PlacementApplicationStatus } from "@src/appV2/PlacementApplications/types/placementApplicationStatus";
import { JobType } from "@src/appV2/PlacementCandidates/types";
import { TIME_SLOTS } from "@src/appV2/Shifts/types";
import { z } from "zod";

import { PLACEMENT_TIER, PlacementStatus, ROTATION_TYPES } from "./createPlacement.schema";
import { jobShiftPayRateSchema, placementPayRateSchema } from "./placementPayRate.schema";

// eslint-disable-next-line no-restricted-syntax
export enum InterviewStatus {
  PENDING = "Pending",
  BOOKED = "Booked",
  CANCELLED = "Cancelled",
  RESCHEDULED = "Rescheduled",
  ATTENDANCE_CONFIRMED = "AttendanceConfirmed",
  NO_CALL_NO_SHOW = "NoCallNoShow",
}

const shiftRotationSchema = z.object({
  type: z.enum(ROTATION_TYPES),
  duration: z.number(),
});

export const GetPlacementsForWorkplaceQuerySchema = z.object({
  page: z
    .object({
      size: z.number().optional(),
      cursor: z.string().optional(),
    })
    .optional(),
});
export type GetPlacementsForWorkplaceQuery = z.infer<typeof GetPlacementsForWorkplaceQuerySchema>;

export const placementDataSchema = z.object({
  type: z.literal(API_TYPES.placement),
  id: z.string(),
  attributes: z.object({
    status: z.nativeEnum(PlacementStatus),
    tier: z.nativeEnum(PLACEMENT_TIER),
    title: z.string(),
    description: z.string().optional(),
    createdAt: z.string().optional(),
    updatedAt: z.string().optional(),
    confirmedAt: z.string().optional(),
    hiringPOC: z
      .object({
        email: z.string(),
        name: z.string(),
      })
      .optional(),
    jobShifts: z.array(
      z.object({
        jobType: z.enum(Object.values(JobType) as [JobType, ...JobType[]]),
        shiftTypes: z.array(z.enum(TIME_SLOTS)),
        shiftTypesWithTime: z
          .array(
            z.object({
              type: z.enum(TIME_SLOTS),
              startTime: z.string().optional(),
              endTime: z.string().optional(),
            })
          )
          .optional(),
        daysOfWeek: z.array(z.enum(Object.values(DayOfWeek) as [DayOfWeek, ...DayOfWeek[]])),
        scheduleType: z.enum(["4_on_2_off", "flexible", "fixed", "other"]).optional(),
        rotation: z.array(shiftRotationSchema).optional(),
        minimumRequiredDaysPerWeek: z.number().optional(),
        workdaysRequired: z.enum(["weekdays", "weekends", "both"]).optional(),
        notes: z.string().optional(),
        enabled: z.boolean().optional(),
        newGradFriendly: z.boolean().optional(),
        transparencyPayRates: jobShiftPayRateSchema.optional(),
      })
    ),
    perks: z.array(z.string()),
    workerTypes: z.array(z.string()),
    validUntil: z.coerce.date().optional(),
    originalJobLink: z.string().optional(),
    webApplicationLink: z.string().optional(),
    payRange: z
      .object({
        minPay: z
          .object({
            amountInMinorUnits: z.number(),
            currencyCode: z.string(),
          })
          .optional(),
        maxPay: z
          .object({
            amountInMinorUnits: z.number(),
            currencyCode: z.string(),
          })
          .optional(),
      })
      .optional(),
    signOnBonus: z
      .object({
        amountInMinorUnits: z.number(),
        currencyCode: z.string(),
      })
      .optional(),
    distance: z.number().optional(),
    address: z.object({
      formatted: z.string().optional(),
    }),
    coordinates: z.array(z.number()),
    schedule: z.string().optional(),
    exemptFromDocumentRequirements: z.boolean().optional(),
    transparencyPayRates: placementPayRateSchema.optional(),
    interviewInstructions: z.string().optional(),
  }),
  relationships: z.object({
    workplace: z.object({
      data: z.object({
        type: z.literal(API_TYPES.workplace),
        id: z.string(),
      }),
    }),
    applications: z
      .object({
        data: z.array(
          z.object({
            type: z.literal(API_TYPES.placementApplication),
            id: z.string(),
          })
        ),
      })
      .optional(),
    tags: z
      .object({
        data: z.array(
          z.object({
            type: z.literal("tag"),
            id: z.string(),
          })
        ),
      })
      .optional(),
    hiringManagers: z
      .object({
        data: z.array(
          z.object({
            type: z.literal(API_TYPES.workplaceUser),
            id: z.string(),
            meta: z
              .object({
                isPrimary: z.boolean().optional(),
              })
              .optional(),
          })
        ),
      })
      .optional(),
    interviews: z
      .object({
        data: z.array(
          z.object({
            type: z.literal(API_TYPES.interview),
            id: z.string(),
          })
        ),
      })
      .optional(),
  }),
});
export type PlacementData = z.infer<typeof placementDataSchema>;

export const placementWorkplaceIncludeSchema = z.object({
  type: z.literal(API_TYPES.workplace),
  id: z.string(),
  attributes: z.object({
    name: z.string(),
  }),
  relationships: z.object({
    workerRequirements: z.object({
      data: z.array(
        z.object({
          type: z.literal(API_TYPES.workerRequirement),
          id: z.string(),
        })
      ),
    }),
  }),
});
export type PlacementIncludedWorkplace = z.infer<typeof placementWorkplaceIncludeSchema>;

const placementApplicationIncludeSchema = z.object({
  type: z.literal(API_TYPES.placementApplication),
  id: z.string(),
  attributes: z.object({
    appliedDate: z.string(),
    status: z.nativeEnum(PlacementApplicationStatus).optional(),
  }),
});
export type PlacementIncludedApplication = z.infer<typeof placementApplicationIncludeSchema>;

const placementInterviewSettingsIncludeSchema = z.object({
  type: z.enum([API_TYPES.interviewSettings]),
  id: z.string().optional(),
  attributes: z.object({
    scheduleInterviews: z.boolean(),
  }),
  relationships: z.object({
    workplace: z.object({
      data: z.object({
        type: z.literal(API_TYPES.workplace),
        id: z.string(),
      }),
    }),
  }),
});
export type PlacementInterviewSettingsInclude = z.infer<
  typeof placementInterviewSettingsIncludeSchema
>;

export const placementInterviewIncludeSchema = z.object({
  type: z.literal(API_TYPES.interview),
  id: z.string(),
  attributes: z.object({
    type: z.nativeEnum(InterviewType).optional(),
    start: z.string(),
    end: z.string(),
    status: z.nativeEnum(InterviewStatus),
  }),
});
export type PlacementIncludedInterview = z.infer<typeof placementInterviewIncludeSchema>;

const placementOpenHouseIncludeSchema = z.object({
  type: z.literal(API_TYPES.openHouse),
  id: z.string(),
  attributes: z.object({
    startDate: z.string(),
    endDate: z.string(),
  }),
});

const hiringEventIncludedSchema = z.object({
  type: z.literal(API_TYPES.hiringEvent),
  id: z.string(),
  attributes: z.object({
    type: z.enum(["OpenHouse", "JobFair"]),
    address: z.object({
      formatted: z.string().optional(),
    }),
    coordinates: z.object({
      latitude: z.number(),
      longitude: z.number(),
    }),
    fixedSchedule: z
      .object({
        startDate: z.string(),
        endDate: z.string(),
        interviewLength: z.number(),
        availableInterviewers: z.number(),
        overfillFirstSlots: z.boolean().optional(),
      })
      .optional(),
  }),
});

// eslint-disable-next-line no-restricted-syntax
enum WorkerRequirementStatus {
  REJECTED = "Rejected",
  EXPIRED = "Expired",
  EXPIRING = "Expiring Soon",
  MISSING = "Missing",
  IN_REVIEW = "In Review",
  APPROVED = "Approved",
}

const placementWorkerRequirementIncludeSchema = z.object({
  type: z.literal(API_TYPES.workerRequirement),
  id: z.string(),
  attributes: z.object({
    status: z.nativeEnum(WorkerRequirementStatus),
    isValid: z.boolean(),
    qualifications: z.array(z.string()),
  }),
  relationships: z.object({
    requirement: z.object({
      data: z.object({
        type: z.literal(API_TYPES.requirement),
        id: z.string(),
      }),
    }),
  }),
});
export type PlacementWorkerRequirementInclude = z.infer<
  typeof placementWorkerRequirementIncludeSchema
>;

const placementRequirementIncludeSchema = z.object({
  type: z.literal(API_TYPES.requirement),
  id: z.string(),
  attributes: z.object({
    name: z.string(),
  }),
});
export type PlacementRequirementInclude = z.infer<typeof placementRequirementIncludeSchema>;

const placementTagIncludeSchema = z.object({
  type: z.literal("tag"),
  id: z.string(),
  attributes: z.object({
    name: z.string(),
    entityType: z.string(),
    metadata: z.record(z.any()),
  }),
});
export type PlacementIncludedTag = z.infer<typeof placementTagIncludeSchema>;

const placementWorkplaceUserIncludedSchema = z.object({
  type: z.literal(API_TYPES.workplaceUser),
  id: z.string(),
  attributes: z.object({
    name: z.string().optional(),
    firstName: z.string().optional(),
    email: z.string().optional(),
    roles: z.array(z.string()).optional(),
    jobTitle: z.string().optional(),
    phone: z.string().optional(),
    welcomeMessageSentAt: z.coerce.date().optional(),
  }),
});
export type PlacementWorkplaceUserIncluded = z.infer<typeof placementWorkplaceUserIncludedSchema>;

export const placementIncludeSchema = z.array(
  z.union([
    placementWorkplaceIncludeSchema,
    placementApplicationIncludeSchema,
    placementInterviewSettingsIncludeSchema,
    placementInterviewIncludeSchema,
    placementOpenHouseIncludeSchema,
    hiringEventIncludedSchema,
    placementWorkerRequirementIncludeSchema,
    placementRequirementIncludeSchema,
    placementTagIncludeSchema,
    placementWorkplaceUserIncludedSchema,
  ])
);

export const paginatedGetPlacementsResponseSchema = z.object({
  data: z.array(placementDataSchema),
  included: placementIncludeSchema,
  links: z.object({
    nextCursor: z.string().optional(),
  }),
  meta: z
    .object({
      placementCountByWorkerType: z.record(z.string(), z.number()),
    })
    .optional(),
});
export type GetPlacementsResponse = z.infer<typeof paginatedGetPlacementsResponseSchema>;

export const PlacementResponseSchema = z.object({
  data: placementDataSchema,
  included: placementIncludeSchema,
});
export type PlacementResponse = z.infer<typeof PlacementResponseSchema>;
